import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { ICalibrationSetting } from '@models/settings/calibration-setting';
import { StoreActions } from '@models/store/store-actions';
import { IStoreState } from '@models/store/store-state';
import { ObservableStore } from '@services/observable-store/dist';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, retry, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CalibrationSettingsService extends ObservableStore<IStoreState> {
  private settingsApiURL: string;

  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfigProvider) {
    super({ trackStateHistory: false });
    this.appConfig.environment.subscribe(env => {
      this.settingsApiURL = `${env.API.SETTINGS}/api/calibrationsettings`;
    });
  }

  public getSettings(forceRefetch = false): Observable<ICalibrationSetting[]> {
    const state = this.getState();
    if (state && state.calibrationSettings && !forceRefetch) {
      return of(state.calibrationSettings);
    } else {
      return this.fetchSettings();
    }
  }

  private fetchSettings(): Observable<ICalibrationSetting[]> {
    return this.http.get<ICalibrationSetting[]>(this.settingsApiURL).pipe(
      tap(calibrationSettings => {
        this.setState({ calibrationSettings }, StoreActions.GetCalibrationSettings);
        return calibrationSettings;
      }),
      retry(3),
      catchError(this.handleError)
    );
  }

  public getSettingByName$(settingName: string, forceRefetch = false): Observable<ICalibrationSetting> {
    const state = this.getState();
    if (state && state.calibrationSettings && !forceRefetch) {
      const setting = state.calibrationSettings.find(({ SettingName }) => SettingName === settingName);
      return of(setting);
    } else {
      return this.fetchSettings().pipe(
        mergeMap(settings => settings),
        filter(setting => setting.SettingName === settingName)
      );
    }
  }

  /**
   * Old version. Use getSettingByName$
   */
  public getSettingByName(settingName: string): Promise<ICalibrationSetting> {
    const state = this.getState();
    if (state && state.calibrationSettings) {
      const setting = state.calibrationSettings.find(({ SettingName }) => SettingName === settingName);
      return Promise.resolve(setting);
    } else {
      return this.getSettings()
        .toPromise()
        .then(() => this.getSettingByName(settingName));
    }
  }

  public updateSettings(settings: ICalibrationSetting[]) {
    return this.http.put<ICalibrationSetting[]>(this.settingsApiURL, settings).pipe(
      tap(calibrationSettings => {
        this.setState({ calibrationSettings }, StoreActions.SetCalibrationSettings);
        return calibrationSettings;
      }),
      retry(3),
      catchError(this.handleError)
    );
  }

  private handleError(error: any) {
    console.error('server error:', error);
    if (error.error instanceof Error) {
      const errMessage = error.error.message;
      return throwError(errMessage);
    }
    return throwError(error || 'Server error');
  }
}
